import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  doc,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../../lib/firestore';
import type { ActiveSession } from '../../types';
import { devError } from '../../utils/dev-log';

/**
 * Service for managing active guest sessions.
 *
 * Sessions live at: properties/{propertyId}/sessions/{sessionId}
 * All queries are scoped to a specific property.
 */
export class SessionsService {
  /**
   * Get the sessions collection ref for a given property.
   */
  private sessionsCollection(propertyId: string) {
    return collection(db, 'properties', propertyId, 'sessions');
  }

  /**
   * Subscribe to all active sessions for a property
   */
  subscribeToActiveSessions(
    propertyId: string,
    onUpdate: (sessions: ActiveSession[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(
      this.sessionsCollection(propertyId),
      where('status', 'in', ['active', 'handover_requested', 'agent_active']),
      orderBy('lastActivity', 'desc'),
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const sessions = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
        } as unknown as ActiveSession));
        onUpdate(sessions);
      },
      (error) => {
        devError('Error subscribing to active sessions:', error);
        onError?.(error);
      }
    );
  }

  /**
   * Subscribe to a specific session
   */
  subscribeToSession(
    propertyId: string,
    sessionId: string,
    onUpdate: (session: ActiveSession | null) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const docRef = doc(this.sessionsCollection(propertyId), sessionId);

    return onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          onUpdate({ id: snap.id, ...snap.data() } as unknown as ActiveSession);
        } else {
          onUpdate(null);
        }
      },
      (error) => {
        devError(`Error subscribing to session ${sessionId}:`, error);
        onError?.(error);
      }
    );
  }

  /**
   * Get active sessions (one-time fetch)
   */
  async getActiveSessions(propertyId: string): Promise<ActiveSession[]> {
    const q = query(
      this.sessionsCollection(propertyId),
      where('status', 'in', ['active', 'handover_requested', 'agent_active']),
      orderBy('lastActivity', 'desc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
    } as unknown as ActiveSession));
  }

  /**
   * Get sessions by status
   */
  async getSessionsByStatus(
    propertyId: string,
    status: ActiveSession['status']
  ): Promise<ActiveSession[]> {
    const q = query(
      this.sessionsCollection(propertyId),
      where('status', '==', status),
      orderBy('lastActivity', 'desc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
    } as unknown as ActiveSession));
  }
}

// Export singleton instance
export const sessionsService = new SessionsService();
